import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Calendar, COUNTRY_CODE, ProductOP } from '@cemex-core/cmx-api';
import { State, getAuth } from '@cemex/cmx-client-layout';
import { Observable, combineLatest, forkJoin, iif, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { DcoFactoryData } from '../dco-factory-data';
import { DcoData, OfferDaysConfigValues, Pump } from '../dco-data';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import {
  getCalendarState,
  getCutOffState,
  getFullSalesAreasState,
} from '@ordersnproducts/modules/multiple-order-online/redux/selectors/multiple-order-online.selector';
import { getSelectedOrderForm } from '@ordersnproducts/modules/multiple-order-online/redux/selectors/orders-forms.selector';
import {
  getPODs,
  getProducts,
  getFeatureTemperatureManagement,
  getAvailableValuesAdded,
  getFeatureIceQuantity,
} from '@ordersnproducts/modules/multiple-order-online/redux/selectors/orders-catalogs.selector';

import { selectActiveShipmentLocationEntity } from 'app/shared/upload-document/redux/selectors/shipment-location.selector';
import { selectActiveShippingConditionEntity } from 'app/shared/upload-document/redux/selectors/shipping-condition.selector';
import { Country, getAppInjector, makeReference } from '../../../../../utils/mix-reference';
import { TemperatureControlEnum } from '@ordersnproducts/modules/shared/models/temperature-control.enum';
import { TEMPERATURE_CONTROL } from '@ordersnproducts/modules/shared/constants/temperature-control';
import IGeoPlace from '@cemex-core/cmx-api/models/geo-place/geo-place.interface';
import { getPointOfDeliveryAddress } from '@ordersnproducts/modules/order-builder/reducers';
import { SHIPMENT_LOCATION_STATUS } from '@ordersnproducts/modules/shared/constants/shipment-location-status';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from '@ordersnproducts/features';
import { prependedValueAdded } from '@ordersnproducts/modules/shared/utils/prepended-value-added';
import {
  BOOM_PUMP,
  BOOM_PUMP_CODE,
  STATIONARY_PUMP,
  STATIONARY_PUMP_CODE,
} from '@ordersnproducts/modules/order-builder/modules/rms-specification/constants/pumps';
import { isPump } from '@ordersnproducts/modules/shared/utils/pump';
import { RMS_REQUEST_TYPES } from '@ordersnproducts/utils';
import { MultipleOrderOnlineService } from '@ordersnproducts/modules/multiple-order-online/multiple-order-online.service';
import { BusinessDay } from '@cemex-core/cmx-api/models/calendars/calendar';
import * as moment from 'moment';
import { getCurrentDay } from '@ordersnproducts/modules/shared/helpers/same-day-order.helper';

@Injectable({
  providedIn: 'root',
})
export class NewOrderDcoFactoryService implements DcoFactoryData {
  country: Country;

  constructor(
    private store: Store<State>,
    private featureToggleService: FeatureToggleService,
    private moos: MultipleOrderOnlineService,
  ) {
    //
    const injector = getAppInjector();
    this.country = Country.getInstance(injector);
    this.country.getCountryCode();
  }

  /**
   * Creates a data object by combining various observables and mapping their values to a DcoData object.
   *
   * @param {number} index - The index used to create the data object.
   * @returns {Observable<DcoData>} An observable that emits a DcoData object.
   */
  createDataObject(index: number): Observable<DcoData> {
    const data$ = combineLatest([
      // constants
      this.getRequestType(),
      this.getUnloadingTime(),
      this.getPumpData(),
      this.nDays(),
      this.getJobsiteCode(),
      this.getUser(),
      this.getShippingCondition(),
      this.getCountryCode(),
      this.getSalesOrganizationCode(),
      // from order form
      this.getSelectedProduct(),
      this.getValueAddedByCountry$(),
      this.selectLoadSize(),
      this.getVolume(),
      this.getDeliveryFrequency(),
      this.productCodes(),
      this.getPODSelected(),
      this.hasTemperatureControl(),
      this.deliveryAddress(),
      this.technicalProduct(this.country.getCountryCodeProperty() as COUNTRY_CODE),
    ]).pipe(
      take(1),
      map(
        ([
          requestType,
          unloadingTime,
          pump,
          nDays,
          jobsiteCode,
          username,
          shippingCondtion,
          countryCode,
          salesArea,
          product,
          selectedAddedValues,
          loadSize,
          volume,
          deliveryFrequency,
          productCodes,
          pod,
          hasTemp,
          address,
          technicalProduct,
        ]) => {
          const newData: DcoData = {
            requestType: requestType as string,
            unloadingTime: unloadingTime as number,
            pump: pump as Pump,
            nDays: nDays as OfferDaysConfigValues,
            jobsiteCode: jobsiteCode as string,
            username: username as string,
            product: product as ProductOP,
            selectedAddedValues: selectedAddedValues as string[],
            loadSize: loadSize as number,
            volume: volume as number,
            deliveryFrequency: deliveryFrequency as number,
            deliveryPoint: pod != null ? pod : Number(jobsiteCode as string),
            shippingConditions: shippingCondtion as string,
            countryCode: countryCode as string,
            salesOrgCode: salesArea as string,
            productCodes: productCodes as string[],
            hasTemperatureControl: hasTemp,
            address,
            technicalProduct,
          } as DcoData;
          return newData;
        },
      ),
      distinctUntilChanged(),
    );
    return data$;
  }

  /**
   * Determines the request type based on the pump capacity field.
   *
   * This method retrieves the selected order form from the store and evaluates
   * whether the order includes a pump. If a pump is present, the request
   * type is set to `RMS_REQUEST_TYPES.CONCRETE_PUMP`. Otherwise, it defaults to
   * `RMS_REQUEST_TYPES.CONCRETE`.
   *
   * @returns An observable of the request type (`RMS_REQUEST_TYPES`).
   */
  private getRequestType(): Observable<RMS_REQUEST_TYPES> {
    const requestType$ = this.store.select(getSelectedOrderForm).pipe(
      map((orderForm) => {
        const hasCompanyPump = !!orderForm.values[SpecFieldNames.PUMP];
        if (hasCompanyPump) {
          return RMS_REQUEST_TYPES.CONCRETE_PUMP;
        } else {
          return RMS_REQUEST_TYPES.CONCRETE;
        }
      }),
    );
    return requestType$;
  }

  /**
   * Retrieves the product object from the catalog state
   * using the selected product in the current active order form.
   * @returns ProductOP
   */
  private getSelectedProduct(): Observable<ProductOP> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    const product$ = this.store.select(getProducts).pipe(
      withLatestFrom(this.store.select(getSelectedOrderForm)),
      map(([availableProducts, orderForm]) => {
        const recipe = _safe(() => orderForm.values);
        const productCode = _safe(() => String(recipe[SpecFieldNames.MIX]));
        const product = availableProducts.find((availableProduct) => {
          if (countryCode === COUNTRY_CODE.UNITED_KINGDOM) {
            return makeReference(availableProduct) === productCode;
          } else {
            return availableProduct.productCode.replace(/^0+/, '') === productCode.replace(/^0+/, '');
          }
        });
        return product;
      }),
    );
    return product$;
  }

  /**
   * Retrieves data from the calendars API response to obtain the config values
   * for the limits of the DCO API request.
   * This is a constant.
   * @returns OfferDaysConfigValues
   */
  public nDays(): Observable<OfferDaysConfigValues> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    const sameDayMOO = this.featureToggleService.isFeatureEnabled(Features.SAME_DAY_MOO);
    let $nDays;

    if (countryCode === COUNTRY_CODE.UNITED_KINGDOM && sameDayMOO) {
      $nDays = combineLatest([
        this.store.select(getCalendarState),
        this.moos.getDcoRequestDatetime(),
        this.store.select(getCutOffState).pipe(filter((cutOffState) => !!cutOffState && !!cutOffState.timeZone)),
      ]).pipe(
        map(([state, selectedDate, cutOffState]) => {
          const nDays = _safe(() => state.offerDaysAfter, 2);
          const nDaysBefore = _safe(() => state.offerDaysBefore, 2);
          const nSlotsToRank = _safe(() => state.offerSlots, 4);

          // @ts-ignore
          const timeZone = cutOffState.timeZone.iANACode;

          const selectedDatePointer = state.businessDays.findIndex((bDay: BusinessDay) => {
            return moment(bDay.date).isSame(selectedDate, 'date');
          });

          const currentTime = getCurrentDay(timeZone);
          const cutOffStateTime = moment(cutOffState.timeCut, 'HH:mm:ss.SSS').tz(timeZone);

          let daysValidationMargined = 3;

          if (currentTime.isAfter(cutOffStateTime)) {
            daysValidationMargined = 4;
          }

          if (selectedDatePointer >= daysValidationMargined) {
            return {
              offerDaysAfter: nDays,
              offerDaysBefore: nDaysBefore,
              offerSlot: nSlotsToRank,
            };
          }

          return {
            offerDaysAfter: 2,
            offerDaysBefore: 0,
            offerSlot: nSlotsToRank,
          };
        }),
      );
    } else {
      $nDays = this.store.select(getCalendarState).pipe(
        map((state) => {
          const nDays = _safe(() => state.offerDaysAfter, 2);
          const nDaysBefore = _safe(() => state.offerDaysBefore, 2);
          const nSlotsToRank = _safe(() => state.offerSlots, 4);
          return {
            offerDaysAfter: nDays,
            offerDaysBefore: nDaysBefore,
            offerSlot: nSlotsToRank,
          };
        }),
      );
    }

    return $nDays;
  }

  private getUnloadingTime() {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      map((orderForm) => {
        const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
        if (countryCode === COUNTRY_CODE.USA) {
          return 13;
        }
        const recipe = orderForm.values;
        const unloadTime = String(recipe[SpecFieldNames.UNLOAD_TIME]);
        if (unloadTime === undefined) {
          return 0;
        }
        return Number(unloadTime.substring(unloadTime.indexOf('-') + 1, unloadTime.length - 2));
      }),
    );
  }

  /**
   * Retrieves pump data based on the country code.
   *
   * @returns {Observable<Pump>} An observable containing pump data.
   *
   * If the country code is MEXICO, it returns additional parameters for the pump.
   * Otherwise, it returns default pump data with all values set to zero or empty.
   */
  private getPumpData(): Observable<Pump> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    if (countryCode === COUNTRY_CODE.MEXICO) {
      return this.pumpAdditionalParametersCalendars();
    }
    return of({
      minLength: 0,
      maxLength: 0,
      type: [],
      pumpingTime: 0,
      installTime: 0,
      pumpWashOnSite: false,
      hoseQty: 0,
      pipeQty: 0,
      crewQty: 0,
    });
  }

  /**
   * Retrieves additional parameters for pump calendars based on the selected order form and calendar state.
   *
   * @returns {Observable<any>} An observable that emits the pump parameters object.
   *
   * The function performs the following steps:
   * 1. Selects the calendar state and the selected order form from the store.
   * 2. Takes the latest values of the calendar and order form.
   * 3. Maps the values to construct the pump parameters object.
   *
   * The pump parameters object is constructed based on the unload method code and pump size code from the order form:
   * - If the unload method code is `BOOM_PUMP`, it finds the corresponding pump size and constructs the pump object
   *  with specific minLength, maxLength, and type.
   * - If the unload method code is `STATIONARY_PUMP`, it finds the corresponding pump size and constructs the pump object
   *  with specific minLength, maxLength, and type.
   * - If neither condition is met, it returns common pump parameters.
   */
  private pumpAdditionalParametersCalendars(): Observable<any> {
    return this.store.select(getCalendarState).pipe(
      withLatestFrom(this.store.select(getSelectedOrderForm)),
      take(1),
      map(([calendar, orderForm]) => {
        const recipe = _safe(() => orderForm.values);
        const unloadMethodCode = recipe[SpecFieldNames.UNLOAD_TYPE];
        const pumpSizeCode = recipe[SpecFieldNames.PUMP];
        const common = this.commonPumpParameters(recipe, calendar);
        if (unloadMethodCode === BOOM_PUMP) {
          const from = calendar.pump.unloadMethods.find((method) => method.unloadMethodCode === BOOM_PUMP_CODE);
          const selected = from.listPumpPen.find((pumpSize) => pumpSize.pumpSizeCode === pumpSizeCode);
          const minLength = selected.flagSpecificSize ? +recipe[SpecFieldNames.PUMP_LENGTH] - 1 : selected.longMin;
          const maxLength = selected.flagSpecificSize ? +recipe[SpecFieldNames.PUMP_LENGTH] : selected.longMax;
          const type = selected.vehicleTypeCode;
          const pumpObject = { minLength: minLength, maxLength: maxLength, type: [type], ...common };
          return pumpObject;
        }
        if (unloadMethodCode === STATIONARY_PUMP) {
          const from = calendar.pump.unloadMethods.find((method) => method.unloadMethodCode === STATIONARY_PUMP_CODE);
          const selected = from.listPumpStationary.find((pumpSize) => pumpSize.pumpSizeCode === pumpSizeCode);
          const minLength = selected.longMin;
          const maxLength = selected.longMax;
          const type = selected.prefixFull;
          const pumpObject = { minLength: minLength, maxLength: maxLength, type: [type], ...common };
          return pumpObject;
        }
        return common;
      }),
    );
  }

  /**
   * Generates common pump fields
   * @param recipe any
   * @param state Calendar
   * @returns hosesQty, pipeQty, crewQty, installTime
   */
  private commonPumpParameters(recipe: any, state: Calendar) {
    const pumpInstallTime = _safe(() => state.pump.pumpInstallTime);
    const pipeInstallTime = _safe(() => state.pump.pipeInstallTime);
    const pipeQuantity = this.getPipes(state, recipe);
    const hoses = _safe(() => (isNaN(recipe[SpecFieldNames.HOSES]) ? 0 : recipe[SpecFieldNames.HOSES]));
    const calculatedInstallationTime =
      pumpInstallTime !== undefined && pipeInstallTime !== undefined
        ? pumpInstallTime + pipeInstallTime * (pipeQuantity + hoses)
        : 30;
    const cleaningOnSite =
      typeof recipe[SpecFieldNames.CLEANING_ON_SITE] === 'string'
        ? recipe[SpecFieldNames.CLEANING_ON_SITE] === 'true'
        : recipe[SpecFieldNames.CLEANING_ON_SITE];
    state.pump.flagLinearMeters;
    return {
      hoseQty: +hoses,
      pipeQty: +pipeQuantity,
      crewQty: 0,
      installTime: calculatedInstallationTime,
      pumpWashOnSite: cleaningOnSite,
    };
  }

  /**
   * Calculates the number of pipes based on the provided calendar configuration
   * and form values. If the calendar's pump does not use linear meters, it directly
   * returns the pipe quantity from the form values. Otherwise, it calculates the
   * number of pipes required by dividing the linear meters by 3 and rounding up
   * to the nearest whole number.
   *
   * @param calendar - The calendar object containing pump configuration details.
   * @param formValues - An object containing form field values, where keys are
   *                     field names and values are their corresponding inputs.
   * @returns The calculated number of pipes.
   */
  private getPipes(calendar: Calendar, formValues: { [fieldName: string]: any }) {
    if (!calendar.pump.flagLinearMeters) {
      return !!formValues[SpecFieldNames.PIPE_QUANTITY] ? formValues[SpecFieldNames.PIPE_QUANTITY] : 0;
    }
    // has linear meters
    return Math.ceil((formValues[SpecFieldNames.LINEAR_METERS] || 0) / 3);
  }

  /**
   * Retrieves the value added by country based on the country code.
   *
   * If the country code is MEXICO, it returns the added values selected for Mexico.
   * Otherwise, it returns the selected added values for other countries.
   *
   * @returns {Observable<string[]>} The added values based on the country code.
   */
  private getValueAddedByCountry$() {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    if (countryCode === COUNTRY_CODE.MEXICO) {
      return this.getAddedValuesFromUI$();
    }
    return this.getSelectedAddedValues();
  }

  /**
   * Retrieves the value added values if there are any,
   * if there are none, an empty array is used instead.
   * @returns string array
   */
  private getSelectedAddedValues(): Observable<string[]> {
    const addedValues$ = combineLatest([
      this.store.select(getSelectedOrderForm),
      this.store.select(getAvailableValuesAdded),
      this.store.select(getCalendarState).pipe(
        take(1),
        map((calendarState) => {
          return calendarState.tempControlFlag;
        }),
      ),
      this.store.select(getFeatureTemperatureManagement),
      this.store.select(getFeatureIceQuantity),
    ]).pipe(
      take(1),
      map(([orderForm, valuesAdded, tempControlFlag, temperatureUnder, iceQuantity]) => {
        const addedValues: string[] = [];
        const recipe = orderForm.values;
        let valueAddedCodeToFind = '';

        let isNoneSelected = false;
        let noneRef = null;
        if (tempControlFlag === 1 || tempControlFlag === 2) {
          valueAddedCodeToFind = TEMPERATURE_CONTROL.ICE;
        } else {
          valueAddedCodeToFind = TEMPERATURE_CONTROL.CORINH;
        }
        let valueAddedLimit = 4;
        if (tempControlFlag !== 1 && !!recipe[SpecFieldNames.TEMPERATURE_UNDER]) {
          // temp under
          noneRef = temperatureUnder.find((feature) => feature.featureCode === TemperatureControlEnum.NONE);
          isNoneSelected = noneRef.featureId === recipe[SpecFieldNames.TEMPERATURE_UNDER];
          const keyName = `${recipe[SpecFieldNames.CONTRACT]}_${recipe[SpecFieldNames.ID]}`;
          if (
            !isNoneSelected &&
            !!valuesAdded[keyName] &&
            !!valuesAdded[keyName][0] &&
            !!valuesAdded[keyName][0].valuesAdded &&
            valuesAdded[keyName][0].valuesAdded.length !== 0
          ) {
            const tmpVA = valuesAdded[keyName][0].valuesAdded.find(
              (valueAdded) => valueAdded.valueAddedCategoryCode === valueAddedCodeToFind,
            );
            if (!!tmpVA && !addedValues.includes(String(tmpVA.valueAddedCode))) {
              addedValues.push(String(tmpVA.valueAddedCode));
            }
          }
        } else if (tempControlFlag === 1 && !!recipe[SpecFieldNames.ICE_QUANTITY]) {
          // ice
          noneRef = iceQuantity.find((feature) => feature.featureCode === TemperatureControlEnum.NONE);
          isNoneSelected = noneRef.featureId === recipe[SpecFieldNames.ICE_QUANTITY];
          valueAddedLimit = 5;
        }

        if (!!recipe[SpecFieldNames.SUMMER_SET]) {
          addedValues.push(String(recipe[SpecFieldNames.SUMMER_SET]));
        }
        for (let i = 0; i < valueAddedLimit; i++) {
          const aux = recipe[SpecFieldNames.VALUE_ADDED + i];
          if (!!aux && !addedValues.includes(String(aux))) {
            addedValues.push(String(aux));
          }
        }
        return addedValues;
      }),
    );
    return addedValues$;
  }

  /**
   * Retrieves the selected added values for the current order form.
   *
   * This method combines the selected order form and the selected product to extract
   * the added values based on specific criteria. It then processes these values to
   * generate an array of added value families.
   *
   * @returns {Observable<string[]>} An observable that emits an array of added value families.
   */
  private getAddedValuesFromUI$(): Observable<string[]> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    return this.store.select(getSelectedOrderForm).pipe(
      switchMap((recipe) =>
        this.getSelectedProduct().pipe(
          map((product) => {
            return { recipe, product };
          }),
        ),
      ),
      map(({ recipe, product }) => {
        const families: string[] = [];
        const baseName = SpecFieldNames.VALUE_ADDED;
        Object.keys(recipe.values).forEach((name) => {
          if (name.substr(0, baseName.length) === baseName) {
            const value = recipe.values[name] as string;
            if (value) {
              families.push(value);
            }
          }
        });
        return { families, product };
      }),
      map(({ families, product }) => this.makeAddedValuesFamiliesArray(families, product, countryCode)),
      take(1),
    );
  }

  /**
   * Select the load size depending on the country
   * MX have not load size field, the value is from calendars response
   * @param formIndex number
   * @params countryCode COUNTRY_CODE
   * @returns Observable<number>
   */
  private selectLoadSize(): Observable<number> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    if (countryCode === COUNTRY_CODE.MEXICO) {
      return this.loadSizeCalendars();
    }
    return this.getLoadSize();
  }

  /**
   * Retrieves the value of load size from the order form,
   * removing the unit name from the stored value.
   * @returns number
   */
  private getLoadSize(): Observable<number> {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      map((orderForm) => {
        const recipe = orderForm.values;
        const loadSizeValue = String(recipe[SpecFieldNames.LOAD_SIZE]);
        const aux = loadSizeValue.replace('yd3', '');
        return Number(aux);
      }),
    );
  }

  /**
   * Get Load Size from the calendars response
   * MX does not have load size field
   * default value 10
   * @returns Observable<number>
   */
  private loadSizeCalendars(): Observable<number> {
    return this.store.select(getCalendarState).pipe(
      map((calendar) => {
        let loadSize = 10;
        if (calendar) {
          loadSize = calendar.loadSize;
        }
        return loadSize;
      }),
      take(1),
    );
  }

  /**
   * Retrieves the value of the order form's quantity.
   * @returns number
   */
  private getVolume(): Observable<number> {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      map((orderForm) => {
        const recipe = orderForm.values;
        const quantityValue = Number(recipe[SpecFieldNames.QUANTITY]);
        return quantityValue;
      }),
    );
  }

  /**
   * Retrieves the load spacing value and converts to a number.
   * @returns number
   */
  private getDeliveryFrequency(): Observable<number> {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      map((orderForm) => {
        const recipe = orderForm.values;
        return Number(recipe[SpecFieldNames.LOAD_SPACING]);
      }),
    );
  }

  /**
   * Retrieves from the state the selected jobsite/shipment location
   * and gets only the code.
   * @returns string
   */
  private getJobsiteCode(): Observable<string> {
    const jobsiteCode$ = this.store.select(selectActiveShipmentLocationEntity).pipe(
      take(1),
      map((shipmentLocation) => {
        return shipmentLocation.shipmentLocationCode;
      }),
    );
    return jobsiteCode$;
  }

  /**
   * Retrieves from the login state data the user email.
   * @returns string
   */
  private getUser(): Observable<string> {
    return this.store.select(getAuth).pipe(
      take(1),
      map((auth) => {
        return auth.profile.userAccount;
      }),
    );
  }

  /**
   * Retrieves the code from the current active shipping condition.
   * @returns string
   */
  private getShippingCondition(): Observable<string> {
    return this.store.select(selectActiveShippingConditionEntity).pipe(
      take(1),
      map((shippingCondition) => _safe(() => shippingCondition.shippingConditionCode)),
    );
  }

  /**
   * Retrieves the country code from the current active shipping condition.
   * @returns string
   */
  private getCountryCode(): Observable<string> {
    return this.store.select(selectActiveShippingConditionEntity).pipe(
      take(1),
      map((shippingCondition) => _safe(() => shippingCondition.countryCode.trim())),
    );
  }

  /**
   * Retrieves from the state the sales org code.
   * @returns string
   */
  private getSalesOrganizationCode(): Observable<string> {
    return this.store.select(getFullSalesAreasState).pipe(
      take(1),
      map((fullSalesArea) => {
        return fullSalesArea.salesOrganizationCode;
      }),
    );
  }

  /**
   * Retrieves an observable that emits an array of product codes based on the country code.
   *
   * This method combines multiple observables to fetch different types of product codes:
   * - Commercial product codes
   * - Technical product codes
   * - Added value product codes
   *
   * The selection of observables for commercial and added value product codes depends on the country code.
   *
   * @returns {Observable<string[]>} An observable that emits an array of product codes.
   */
  private productCodes(): Observable<string[]> {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    const commProduct$ = iif(
      () => [COUNTRY_CODE.UNITED_KINGDOM].includes(countryCode),
      // if true
      this.commProductMixReference(),
      // if false
      this.commProduct(),
    );
    const addedValues$ = iif(
      () => [COUNTRY_CODE.UNITED_KINGDOM].includes(countryCode as COUNTRY_CODE),
      // if true
      this.addedValuesMixReference(),
      // if false
      this.valueAddedinProductCodes$(),
    );
    return forkJoin({
      commProduct: commProduct$,
      technicalProduct: this.technicalProduct(countryCode),
      addedValues: addedValues$,
    }).pipe(
      map((response) => {
        const aux = [];
        // RMX UL 5.2 do not have to send the technical product as value added
        if (response.technicalProduct && countryCode !== COUNTRY_CODE.MEXICO) {
          aux.push(response.technicalProduct);
        }
        response.addedValues.forEach((value) => {
          aux.push(value);
        });
        return aux;
      }),
    );
  }

  /**
   * Retrieves the added values for product codes based on the country code.
   * If the country code is MEXICO, it fetches the added values from the UI.
   *
   * @returns An observable containing the added values for the product codes.
   */
  public valueAddedinProductCodes$() {
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    if (countryCode === COUNTRY_CODE.MEXICO) {
      this.getAddedValuesFromUI$();
    }
    return this.addedValues(countryCode);
  }

  private commProductMixReference(): Observable<string> {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      switchMap((recipe) => {
        return this.store.select(getProducts).pipe(
          map((products) => {
            return products.find((available) => makeReference(available) === recipe.values[SpecFieldNames.MIX]);
          }),
          take(1),
        );
      }),
      map((product) => {
        if (!!product) {
          return product.productCode;
        }
        return '';
      }),
      take(1),
    );
  }

  private commProduct(): Observable<string> {
    return this.store.select(getSelectedOrderForm).pipe(
      map((recipe) => recipe.values[SpecFieldNames.MIX]),
      switchMap((code) =>
        this.store.select(getProducts).pipe(
          map((products) => products.find((product) => makeReference(product) === code)),
          take(1),
        ),
      ),
      map((product) => _safe(() => product.productCode)),
      take(1),
    );
  }

  /**
   * Get the value added from the contract
   * and prepend if the country has mandatory value added
   * @returns string[]
   */
  private addedValuesMixReference(): Observable<string[]> {
    const form$ = this.store.select(getSelectedOrderForm).pipe(take(1));
    const products$ = this.store.select(getProducts).pipe(take(1));
    const countryCode = this.country.getCountryCodeProperty() as COUNTRY_CODE;
    return combineLatest([form$, products$]).pipe(
      map(([form, availableProducts]) => {
        const productCode = form.values[SpecFieldNames.MIX];
        const product = availableProducts.find((available) => {
          if ([COUNTRY_CODE.UNITED_KINGDOM, COUNTRY_CODE.FRANCE].includes(countryCode as COUNTRY_CODE)) {
            return makeReference(available) === productCode;
          } else {
            return available.productCode.replace(/^0+/, '') === productCode;
          }
        });
        return { recipe: form, product };
      }),
      map(({ recipe, product }) => {
        const families: string[] = [];
        product.contracts[0].valueAddeds.forEach((item) => families.push(item.valueAddedCode));
        return { families, product };
      }),
      map(({ families, product }) => this.makeAddedValuesFamiliesArray(families, product, countryCode)),
      take(1),
    );
  }

  private makeAddedValuesFamiliesArray(families: string[], product: ProductOP, countryCode) {
    const valueAddedSelected = !!families && families.length > 0;
    let preprendedVaps: string[] = [];
    if (
      !!product &&
      product.hasMandatoryValueAdded === true &&
      valueAddedSelected &&
      countryCode === COUNTRY_CODE.MEXICO
    ) {
      preprendedVaps = prependedValueAdded(product);
    }
    const aux = [];
    preprendedVaps.forEach((vap) => {
      aux.push(vap);
    });
    families.forEach((family) => {
      aux.push(family);
    });
    return aux.filter((_) => !!_);
  }

  /**
   * Get the value added from the selection on the form (UI)
   * and prepend if the country has mandatory value added
   * @param countryCode
   * @returns string[]
   */
  private addedValues(countryCode): Observable<string[]> {
    const form$ = this.store.select(getSelectedOrderForm).pipe(take(1));
    const products$ = this.store.select(getProducts).pipe(take(1));
    return combineLatest([form$, products$]).pipe(
      map(([form, availableProducts]) => {
        const productCode = form.values[SpecFieldNames.MIX];
        const product = availableProducts.find((available) => {
          return makeReference(available) === productCode;
        });
        return { recipe: form, product };
      }),
      map(({ recipe, product }) => {
        const families: string[] = [];
        const baseName = 'addedProduct';
        Object.keys(recipe).forEach((name) => {
          if (name.substr(0, baseName.length) === baseName) {
            const value: string = recipe[name];
            if (value) {
              families.push(value);
            }
          }
        });
        return { families, product };
      }),
      map(({ families, product }) => this.makeAddedValuesFamiliesArray(families, product, countryCode)),
      take(1),
    );
  }

  /**
   * Retrieves the technical product code based on the provided country code.
   *
   * @param countryCode - The country code to determine which technical product code to retrieve.
   *
   * @returns An Observable that emits the technical product code as a string.
   *
   * The method behaves differently based on the country code:
   * - For `COUNTRY_CODE.MEXICO`, it retrieves the last technical product on value added.
   * - For `COUNTRY_CODE.USA`, it retrieves the last technical product.
   * - For other country codes (e.g., UK), it retrieves the last technical product mix reference.
   */
  private technicalProduct(countryCode: COUNTRY_CODE): Observable<string> {
    if (countryCode === COUNTRY_CODE.MEXICO) {
      return this.lastTechnicalProductOnValueAdded$().pipe(
        switchMap((technicalProduct) => {
          if (!!technicalProduct && !!technicalProduct.technicalProductCode) {
            return of(technicalProduct.technicalProductCode);
          }
          return this.getLastTechnicalProductOfSelectedProduct$();
        }),
      );
    }
    if (countryCode === COUNTRY_CODE.USA) {
      return this.lastTechnicalProduct().pipe(
        map((technicalProduct) => {
          return technicalProduct && technicalProduct.technicalProductCode;
        }),
      );
    } else {
      // FOR UK
      return this.lastTechnicalProductMixReference().pipe(
        map((technicalProductMixReference) => {
          return technicalProductMixReference && technicalProductMixReference.technicalProductCode;
        }),
      );
    }
  }

  private hasTemperatureControl() {
    const form$ = this.store.select(getSelectedOrderForm).pipe(take(1));
    const temperature$ = this.store.select(getFeatureTemperatureManagement).pipe(take(1));
    return combineLatest([form$, temperature$]).pipe(
      map(([orderForm, temperatureUnder]) => {
        const temperatureUnderValue = orderForm.values[SpecFieldNames.TEMPERATURE_UNDER];
        const temperatureUnderObjet = temperatureUnder.find(
          (temperature) => temperature.featureId === temperatureUnderValue,
        );

        return (
          !!orderForm.values[SpecFieldNames.ICE_QUANTITY] ||
          (!!temperatureUnderObjet && temperatureUnderObjet.featureCode !== TemperatureControlEnum.NONE)
        );
      }),
    );
  }

  private lastTechnicalProduct() {
    const technicalProduct$ = this.store.select(getSelectedOrderForm).pipe(
      take(1),
      switchMap((recipe) => {
        const productCode = recipe.values[SpecFieldNames.MIX];
        return this.store.select(getProducts).pipe(
          filter((availables) => !!availables.length),
          map((availables) => availables.find((available) => available.productCode.replace(/^0+/, '') === productCode)),
          map((productSelected) => productSelected.technicalProducts[0]),
          take(1),
        );
      }),
    );
    return technicalProduct$;
  }

  /**
   * Retrieves the last technical product based on the value added from the form values.
   *
   * @returns An observable that emits the last technical product based on the value added.
   *
   * The function performs the following steps:
   * 1. Constructs a keyname using the contract and ID from the form values.
   * 2. Fetches available value-added products (VAPs) from the contract using the constructed keyname.
   * 3. Filters out VAPs that have null values for both `valuesAdded` and `technicalProducts`.
   * 4. Retrieves the last available VAP.
   * 5. Extracts the technical products from the last available VAP.
   * 6. Determines the appropriate technical product based on the unload type:
   *    - If the unload type indicates a pump, selects a pumpable technical product.
   *    - Otherwise, selects the first non-pumpable technical product.
   */
  private lastTechnicalProductOnValueAdded$() {
    const lastTechnicalProduct$ = this.store.select(getSelectedOrderForm).pipe(
      take(1),
      switchMap((orderForm) =>
        this.store.select(getAvailableValuesAdded).pipe(
          map((availables) => {
            const keynameValueAddeds = _safe(
              () => orderForm.values[SpecFieldNames.CONTRACT] + '_' + orderForm[SpecFieldNames.ID],
              undefined,
            );
            return { keynameValueAddeds, availables };
          }),
          map(({ keynameValueAddeds, availables }) => {
            return {
              availables:
                keynameValueAddeds &&
                availables &&
                availables[keynameValueAddeds] &&
                availables[keynameValueAddeds].filter((available) => {
                  if (available.valuesAdded === null && available.technicalProducts === null) {
                    return false;
                  } else {
                    return true;
                  }
                }),
            };
          }),
          map(({ availables }) => {
            const last = _safe(() => availables[availables.length - 1]);
            return { last };
          }),
          map(({ last }) => {
            const technicals = last && last.technicalProducts;
            return { technicals };
          }),
          map(({ technicals }) => {
            const unloadTypeValue = _safe(() => orderForm.values[SpecFieldNames.UNLOAD_TYPE]) as string;

            const hasPump = isPump(unloadTypeValue);

            // If the user selects Pump, needs a pumpable technical product.
            if (!!hasPump) {
              return technicals && technicals.find((technical) => technical.isPumpable === hasPump);
            }
            // Select the first technical product as default regardless the unload type.
            return technicals && technicals.length && technicals[0];
          }),
          take(1),
        ),
      ),
    );
    return lastTechnicalProduct$;
  }

  /**
   * Retrieves the last technical product code of the selected product based on the user's selection.
   *
   * This method selects the current order form and extracts the product code from the form values.
   * It then finds the corresponding product from the store and determines the appropriate technical product code.
   *
   * If the user selects a pump, it returns the technical product code of a pumpable technical product.
   * Otherwise, it returns the technical product code of the first non-pumpable technical product.
   *
   * @returns An observable that emits the technical product code of the selected product.
   */
  private getLastTechnicalProductOfSelectedProduct$() {
    return this.store.select(getSelectedOrderForm).pipe(
      take(1),
      switchMap((recipe) => {
        const productCode = recipe.values[SpecFieldNames.MIX];
        return this.store.select(getProducts).pipe(
          map((products) => products.find((product) => makeReference(product) === productCode)),
          take(1),
          map((product) => {
            const technicals = product.technicalProducts;
            const unloadTypeValue = recipe.values[SpecFieldNames.UNLOAD_TYPE] as string;
            const hasPump = isPump(unloadTypeValue);
            // If the user selects Pump, needs a pumpable technical product.
            if (!!hasPump) {
              return _safe(
                () =>
                  technicals && technicals.find((technical) => technical.isPumpable === hasPump).technicalProductCode,
              );
            }
            // If the user does not selects Pump,
            // Select the first technical product not pumpable as default.
            return _safe(
              () => technicals && technicals.find((technical) => technical.isPumpable === false).technicalProductCode,
            );
          }),
        );
      }),
    );
  }

  private lastTechnicalProductMixReference() {
    const technicalProductMixReference$ = this.store.select(getSelectedOrderForm).pipe(
      take(1),
      switchMap((recipe) => {
        const productCode = recipe.values[SpecFieldNames.MIX];
        return this.store.select(getProducts).pipe(
          filter((availables) => !!availables.length),
          map((availables) => availables.find((available) => makeReference(available) === productCode)),
          map((productSelected) => productSelected.technicalProducts[0]),
          take(1),
        );
      }),
    );
    return technicalProductMixReference$;
  }

  private getPODSelected(): Observable<number> {
    const country = this.country.getCountryCodeProperty();
    const variosLocationToggle = this.featureToggleService.isFeatureEnabled(Features.EnablePODDropdownFeature);
    const form$ = this.store.select(getSelectedOrderForm).pipe(take(1));
    const pods$ = this.store.select(getPODs).pipe(take(1));
    if (country === COUNTRY_CODE.UNITED_KINGDOM) {
      return combineLatest([form$, pods$]).pipe(
        map(([form, pods]) => {
          const podId = form.values[SpecFieldNames.POINT_OF_DELIVERY];
          const pod = pods.find((podv) => podv.shipmentLocationId === podId);
          if (pod) {
            const numberShipmentLocationCode = parseInt(pod.shipmentLocationCode.replace(/\D/g, ''), 10);
            return numberShipmentLocationCode;
          } else {
            return null;
          }
        }),
      );
    } else if (variosLocationToggle) {
      return combineLatest([form$, pods$]).pipe(
        withLatestFrom(this.getJobsiteCode()),
        map(([[form, pods], jobsiteCode]) => {
          const podId = form.values[SpecFieldNames.POINT_OF_DELIVERY];
          const selected = pods.find((podv) => podv.shipmentLocationId === podId);
          if (!selected) {
            return null;
          }
          if (
            selected.shipmentLocationStatus.shipmentLocationStatusCode !== SHIPMENT_LOCATION_STATUS.ACTIVE &&
            selected.shipmentLocationCode !== jobsiteCode
          ) {
            return Number(0);
          }
          return Number(selected.shipmentLocationCode);
        }),
      );
    } else {
      return of(null);
    }
  }

  /**
   *
   * @returns IGeoPlace
   */
  private deliveryAddress(): Observable<IGeoPlace> {
    const address$ = this.store.select(getPointOfDeliveryAddress).pipe(
      take(1),
      map((response) => {
        return !!response ? response.geoPlace : { latitude: 0, longitude: 0 };
      }),
    );
    return address$;
  }
}
